interface SSOMatch {
  (routeMap: Record<string, string>, url?: string): {
    destination: string | null
    isMatch: boolean
  }
}

function addTrailingSlash(pathname: string): string {
  if (!/\/$/.test(pathname)) return `${pathname}/`
  return pathname
}

function sanitizePathname(pathname: string): string {
  return addTrailingSlash(pathname).toLowerCase()
}

/**
 * Matches a route map with the current url. Will validate that
 * required query params exist on current URL
 *
 * ```tsx
 * // Example URL -> `https://example.com/red/car`
 *
 * const { destination } = findSSOMatch({ '/red/car': 'turbo-car' }) // => 'turbo-car'
 * const { destination } = findSSOMatch({ '/blue/boat': 'turbo-car' }) // => null
 * const { isMatch } = findSSOMatch({ '/red/car': 'turbo-car' }) // => true
 * const { isMatch } = findSSOMatch({ '/bill/nye': 'turbo-car' }) // => false
 *
 * // Example URL -> `https://example.com/dog?name=spot&ID=1234`
 * const { destination } = findSSOMatch({ '/dog?ID=1234': 'brown-dog' }) // => 'brown-dog'
 * const { destination } = findSSOMatch({ '/dog?color=pink': 'pink-dog' }) // => null
 * ```
 */

export const findSSOMatch: SSOMatch = (
  routeMap,
  url = window.location.href,
) => {
  /**
   * Create a new URL object based on url argument. This will throw an error if
   * user passes invalid URL
   */
  const currentURL = new URL(url)

  /**
   * First lets grab all the possible matching paths, excluding
   * any query params.
   */
  const matchedPaths = Object.entries(routeMap).filter(([path]) => {
    return (
      sanitizePathname(path.split('?')[0]) ===
      sanitizePathname(currentURL.pathname)
    )
  })

  /**
   * Loop through each possible matched path, check to see if there are
   * any required query parameters, and return the first item that fits
   * the criteria. Returns undefined if there are no matched paths.
   */
  const matchedItem = matchedPaths.find(([key, value]) => {
    const matchedURL = new URL(key, currentURL.origin)

    /**
     * Does the matched path need query params?
     */
    const requiresQueryParams = matchedURL.search !== ''

    /**
     * No query params are required, return the first match
     */
    if (!requiresQueryParams) {
      return [key, value]
    }

    /**
     * There are some required query params, lets get those into an array
     */
    const requiredParams = matchedURL.search.replace('?', '').split('&')

    /**
     * Now lets check to see if the current URL query params exist in
     * the requiredParams
     */
    const queryParamsMatch = requiredParams.every(param => {
      /**
       * Get all current query params and make sure that the
       * required query params exist exactly within the current URLs
       */
      const currentURLParams = currentURL.search.replace('?', '').split('&')
      return currentURLParams.includes(param)
    })

    /**
     * If queryParamsMatch is true, then we found our winner.
     */
    if (queryParamsMatch) {
      return [key, value]
    }

    /**
     * Looks like the query params didn't match, return
     */
    return null
  })

  /**
   * Check to see if there were any matching paths and query params
   */
  if (matchedItem) {
    return {
      destination: matchedItem[1],
      isMatch: true,
    }
  }

  /**
   * Default response. No matches found
   */
  return {
    destination: null,
    isMatch: false,
  }
}
